import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PartnerSidebar from '../components/partner/PartnerSidebar'
import PartnerHeader from '../components/partner/PartnerHeader'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import { partnerApiService } from '@/services/partnerApiService'

interface Partner {
  id: number
  name: string
  company?: string
  can_view_users: boolean
  can_view_trading: boolean
  can_view_deposits: boolean
  can_view_withdrawals: boolean
  can_view_fees: boolean
  can_view_statistics: boolean
}

interface PartnerLayoutProps {
  children?: React.ReactNode
}

const PartnerLayout = ({ children }: PartnerLayoutProps) => {
  const { t } = useTranslation('common')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [partner, setPartner] = useState<Partner | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen)

  // 파트너 정보 가져오기
  useEffect(() => {
    let isCancelled = false // cleanup flag to prevent duplicate calls
    
    const fetchPartnerInfo = async () => {
      try {
        console.log('🔍 PartnerLayout: 파트너 정보 요청 시작')
        const partnerData = await partnerApiService.getProfile()
        console.log('✅ PartnerLayout: 파트너 정보 가져오기 성공', partnerData)
        
        if (!isCancelled) {
          setPartner(partnerData)
        }
      } catch (err) {
        console.error('❌ PartnerLayout: 파트너 정보 가져오기 실패:', err)
        if (!isCancelled) {
          setError(err instanceof Error ? err.message : t('unknownError'))
          // 인증 실패시 로그인 페이지로 리다이렉트
          window.location.href = '/login'
        }
      } finally {
        if (!isCancelled) {
          setLoading(false)
        }
      }
    }

    fetchPartnerInfo()
    
    // cleanup function
    return () => {
      isCancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !partner) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">{t('error')}</h1>
          <p className="text-dark-400 mb-6">{error || t('cannotLoadPartnerInfo')}</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            {t('goToLogin')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex bg-dark-950 h-screen overflow-hidden">
      {/* Desktop Sidebar - Fixed */}
      <div className="hidden lg:block w-52 relative z-40 flex-shrink-0">
        <PartnerSidebar isOpen={true} onClose={() => {}} partner={partner} />
      </div>

      {/* Mobile Sidebar - Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ x: -208 }}
            animate={{ x: 0 }}
            exit={{ x: -208 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed inset-y-0 left-0 w-52 z-50 lg:hidden"
          >
            <PartnerSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} partner={partner} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/90 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header */}
        <PartnerHeader onMenuClick={toggleSidebar} partner={partner} />

        {/* Main content - Scrollable */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          {children || <Outlet context={{ partner }} />}
        </main>
      </div>
    </div>
  )
}

export default PartnerLayout